import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import {
  LayoutDashboard, PhoneCall, Gauge, BarChart3, Trophy, Target, Briefcase, Settings as SettingsIcon,
  Menu, X, LogOut, ChevronDown, Activity,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TABS = [
  { id: "dashboard", label: "Owner Dashboard", icon: LayoutDashboard },
  { id: "daily", label: "Daily Entry", icon: PhoneCall },
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "leaderboard", label: "Scoring", icon: Trophy },
  { id: "goals", label: "Goals", icon: Target },
  { id: "manager", label: "Manager", icon: Briefcase },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

function initials(name) {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function NavButton({ t, mobile, active, onClick }) {
  const Icon = t.icon;
  return (
    <button
      data-testid={mobile ? `nav-${t.id}-mobile` : `nav-${t.id}`}
      onClick={onClick}
      className={`flex items-center gap-2.5 rounded-xl text-sm font-semibold transition-colors duration-150 ${
        mobile ? "w-full px-4 py-3" : "px-3 py-2"
      } ${active ? "bg-[#C9A24B] text-[#0b1120] shadow-sm shadow-[#C9A24B]/25" : "text-slate-300 hover:bg-white/10"}`}
    >
      <Icon className="h-4 w-4" /> {t.label}
    </button>
  );
}

export default function Layout({ tab, setTab, children }) {
  const { currentUser, logout, activeEmployees, selectUser } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const go = (id) => { setTab(id); setMobileOpen(false); };

  return (
    <div className="min-h-screen bg-[#0b1120]">
      <header className="sticky top-0 z-40 bg-[#0b1120]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <button className="xl:hidden text-slate-300" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <div className="h-9 w-9 rounded-lg bg-[#C9A24B] flex items-center justify-center text-[#0b1120]">
              <Activity className="h-5 w-5" />
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="font-heading font-semibold text-white tracking-wide">AuraMed OT CRM</div>
              <div className="text-[10px] text-slate-500">Sales & Telecalling Intelligence</div>
            </div>
          </div>

          <nav className="hidden xl:flex items-center gap-1">
            {TABS.map((t) => <NavButton key={t.id} t={t} active={tab === t.id} onClick={() => go(t.id)} />)}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full border border-white/15 hover:border-[#C9A24B]/40 transition-colors">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#C9A24B] to-[#a07d33] text-[#0b1120] flex items-center justify-center text-xs font-bold">
                  {initials(currentUser.name)}
                </div>
                <span className="text-sm font-semibold text-slate-200 hidden sm:block max-w-[120px] truncate">{currentUser.name}</span>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <div className="px-2 py-1.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Switch team member</div>
              <div className="max-h-64 overflow-y-auto scrollbar-thin">
                {activeEmployees.map((e) => (
                  <DropdownMenuItem key={e.id} data-testid={`switch-user-${e.id}`} onClick={() => selectUser(e)} className="cursor-pointer">
                    <div className="h-6 w-6 rounded-full bg-white/10 text-slate-300 flex items-center justify-center text-[10px] font-bold mr-2">{initials(e.name)}</div>
                    <span className="flex-1">{e.name}</span>
                    <span className="text-[10px] text-slate-500 capitalize">{e.role}</span>
                  </DropdownMenuItem>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer text-rose-400">
                <LogOut className="h-4 w-4 mr-2" /> Exit to name screen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {mobileOpen && (
          <div className="xl:hidden border-t border-white/10 bg-[#0b1120] px-3 py-3 space-y-1 animate-fade-up">
            {TABS.map((t) => <NavButton key={t.id} t={t} mobile active={tab === t.id} onClick={() => go(t.id)} />)}
          </div>
        )}
      </header>

      <main className="max-w-[1500px] mx-auto px-4 py-6 sm:py-8">{children}</main>
    </div>
  );
}
